import React from 'react'
import { motion } from 'framer-motion'
import { Bot, User, Copy, Check } from 'lucide-react'
import { ChatMessage as ChatMessageType } from '../../types/ai-budget'
import ReactMarkdown from 'react-markdown'

interface ChatMessageProps {
  message: ChatMessageType
  isLatest?: boolean
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isLatest = false }) => {
  const [copied, setCopied] = React.useState(false)
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy message:', error)
    }
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Don't render system messages in the UI
  if (isSystem) {
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        duration: 0.4,
        type: "spring",
        stiffness: 100,
        damping: 15
      }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} group`}
    >
      <div className={`flex max-w-[85%] ${isUser ? 'flex-row-reverse' : 'flex-row'} items-start space-x-3`}>
        {/* Avatar */}
        <div className={`flex-shrink-0 ${isUser ? 'ml-3' : 'mr-3'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
            isUser
              ? 'bg-accent-500'
              : 'bg-gradient-to-br from-accent-500 to-accent-600'
          }`}>
            {isUser ? (
              <User className="w-4 h-4 text-white" />
            ) : (
              <Bot className="w-4 h-4 text-white" />
            )}
          </div>
        </div>

        {/* Message Content */}
        <div className={`relative ${isUser ? 'text-right' : 'text-left'}`}>
          {/* Message Bubble */}
          <div
            className={`relative px-4 py-3 rounded-2xl max-w-full ${
              isUser
                ? 'bg-accent-500 text-white'
                : 'bg-background-800 text-background-100 border border-background-700'
            }`}
          >
            {/* Message Content with Markdown Support for AI Messages */}
            <div className="prose prose-sm max-w-none">
              {isUser ? (
                <p className="whitespace-pre-wrap break-words m-0">{message.content}</p>
              ) : (
                <ReactMarkdown
                  components={{
                    // Custom styling for markdown elements
                    p: ({ children }) => (
                      <p className="mb-2 last:mb-0 text-background-100">{children}</p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-accent-300">{children}</strong>
                    ),
                    ul: ({ children }) => (
                      <ul className="mb-2 pl-4 space-y-1">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="mb-2 pl-4 space-y-1">{children}</ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-background-200">{children}</li>
                    ),
                    code: ({ children }) => (
                      <code className="bg-background-700 text-accent-300 px-1 py-0.5 rounded text-sm">
                        {children}
                      </code>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-2 border-accent-500 pl-3 italic text-background-300">
                        {children}
                      </blockquote>
                    ),
                    h1: ({ children }) => (
                      <h1 className="text-lg font-bold mb-2 text-background-100">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-base font-bold mb-2 text-background-100">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-sm font-bold mb-1 text-background-100">{children}</h3>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              )}
            </div>

            {/* Copy Button for AI Messages */}
            {!isUser && (
              <button
                onClick={handleCopy}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-background-700 rounded"
                title="Copy message"
              >
                {copied ? (
                  <Check className="w-3 h-3 text-green-400" />
                ) : (
                  <Copy className="w-3 h-3 text-background-400" />
                )}
              </button>
            )}
          </div>

          {/* Timestamp */}
          <div className={`mt-1 text-xs text-background-500 ${isUser ? 'text-right' : 'text-left'}`}>
            {formatTime(message.created_at)}
            {message.metadata?.edited && (
              <span className="ml-1 italic">(edited)</span>
            )}
          </div>

          {/* Special Indicators */}
          {isLatest && !isUser && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute -bottom-1 left-0 w-2 h-2 bg-accent-500 rounded-full"
            />
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default ChatMessage

